import { ThetaError } from "@theta/api-client";

/**
 * A REFUSAL IS AN ANSWER — retrying it is asking the same question twice
 *
 * `lib/query-client.ts` sets a global `retry: 1`, which is right for the thing
 * retries are for: a dropped connection, a 502 from a restarting pod, a
 * request that timed out on the way. It is wrong for every 4xx on this API,
 * because /api/v1 answers 4xx only when it has DECIDED something:
 *
 *   401 `unauthenticated`      you are not signed in — still true 300ms later
 *   403 `forbidden`            staff-only, and you are not staff
 *   404 `notFound`             D7: also what "you may not see this" looks like
 *   409 `conflict`             somebody else already resolved it
 *   422 `validationFailed`     the body is wrong, and it is the same body
 *   429 `rateLimited`          a retry is the one thing that makes it worse
 *
 * Nineteen call sites had spelled this out by hand as
 * `(n, e) => !isThetaError(e, "notFound") && n < 2` — sixteen inline at the
 * `retry:` site, three as a private `retryUnlessAnswered` — each naming the one
 * code it had thought about, and the two admin hooks were written without it.
 * So an ordinary member who followed a stale `/admin` link sent TWO
 * `GET /admin/stats` requests and got two correct 403s before the screen
 * settled on "Ingen adgang"; and every one of the nineteen retried a 401, a 422
 * and a 429 it had never considered.
 *
 * OSO-617 swept all nineteen onto this function. `scripts/check-ratchets.mjs`
 * holds the shape at a baseline of ZERO, because it is what anybody writes when
 * they copy the nearest query and adjust it.
 *
 * The rule here is the general one and it is stated once: a 4xx is a decision
 * and is never retried; anything else — a 5xx, or a transport failure with no
 * status at all — is tried up to `MAX_RETRIES` times.
 *
 * WHY `status` AND NOT A LIST OF CODES. `ProblemCode` is an OPEN enum (D15):
 * the server may add a value in any deploy, and a client that switched on the
 * names would treat every new refusal as retryable. The status class is the
 * part of the contract that cannot grow a new meaning.
 */
const MAX_RETRIES = 2;

export function retryUnlessRefused(failureCount: number, error: unknown): boolean {
  if (error instanceof ThetaError && error.status >= 400 && error.status < 500) {
    return false;
  }
  return failureCount < MAX_RETRIES;
}
